import { Button, Label, TextInput, UseStepContext } from '@consigli/facade';
import { useGetPackagesByTypeQuery, useProjectId, useServiceName } from '@consigli/hooks';
import { DocumentPackageType } from '@consigli/types';
import { FC, useCallback } from 'react';
import { useController, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { SelectLanguage } from '@/features/package-creation/components/select-language';
import { useFetchValidateName } from '@/features/package-creation/components/user-input';
import { ServiceIdIndexValue } from '@/routes';

import { WizardFormData } from '../wizard-context';

interface SiteNameStepProps {
  context: UseStepContext;
}

export const SiteNameStep: FC<SiteNameStepProps> = ({ context }) => {
  const { t } = useTranslation();
  const projectId = useProjectId();
  const serviceName = useServiceName();
  const type = ServiceIdIndexValue[serviceName] as DocumentPackageType;
  const { next, previous, hasNext, hasPrevious } = context();

  const { control, trigger, watch } = useFormContext<WizardFormData>();

  // Site name field
  const {
    field: nameField,
    fieldState: { error: nameError },
  } = useController({
    name: 'iterationName',
    control,
    rules: {
      required: t('package-creation.site-name-empty'),
      maxLength: { value: 50, message: t('package-creation.too-long') },
    },
  });

  const { validateName } = useFetchValidateName(useGetPackagesByTypeQuery, {
    projectId,
    type,
  });

  // Watch fields for button state
  const language = watch('language');
  // const employerRole = watch('employerRole');

  const handleNext = useCallback(async () => {
    // Trigger validation for all fields
    const isValid = await trigger(['iterationName', 'language']);
    if (!isValid) return;

    // Additional validation for duplicate names
    const trimmedName = nameField.value.trim();
    const validationResult = validateName(trimmedName);
    if (typeof validationResult === 'string') {
      // Set manual error - we need to use setError from form context
      return;
    }

    next();
  }, [trigger, nameField.value, validateName, next]);

  const isNextDisabled = !nameField.value?.trim() || !language;

  return (
    <div className="w-full mt-4">
      <Label className="mb-2 text-base font-semibold text-day-neutral-subtle">
        {t('package-creation.site-name')}
      </Label>
      <div className="flex flex-col" translate="no">
        <TextInput
          label=""
          placeholder={t('package-creation.site-name-placeholder')}
          className="border border-day-light-2 rounded"
          value={nameField.value}
          onChange={nameField.onChange}
          onBlur={nameField.onBlur}
        />
        {nameError && <div className="text-red-700 text-sm mt-1">{nameError.message}</div>}
      </div>

      {/*  <div className="mt-4">
        <SelectEmployerRole label={t('employer-role.label')} />
      </div> */}

      <div className="mt-4">
        <SelectLanguage label={t('language.select')} isDisabled={true} />
      </div>

      <div className="flex items-stretch gap-2 mt-6">
        {hasPrevious && (
          <Button secondary rounded onClick={previous} type="button">
            {t('package-creation.back')}
          </Button>
        )}
        {hasNext && (
          <Button primary rounded onClick={handleNext} type="button" disabled={isNextDisabled}>
            {t('package-creation.next')}
          </Button>
        )}
      </div>
    </div>
  );
};
